import { useRef, useState } from "react";
import { toast } from "sonner";
import { Clock, Scissors, Timer } from "lucide-react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

const needsCheck = (d) =>
  d.video_mode === "fast" && d.video_model === "h3-turbo" && (d.voice_option || "none") === "none" && !d.scene_parts;

const Choice = ({ testId, icon: Icon, title, text, onClick }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    className="flex w-full items-start gap-3 rounded-lg border border-white/10 bg-white/5 p-4 text-left transition-colors hover:border-[#00F0FF]/60 hover:bg-[#00F0FF]/5">
    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#00F0FF]" />
    <span>
      <span className="block font-medium text-white">{title}</span>
      <span className="block text-sm text-muted-foreground">{text}</span>
    </span>
  </button>
);

// Busy scenes (walk in, order, sit down, then talk) are made in parts; ask before they run past the chosen length
export const useLengthChoice = () => {
  const [plan, setPlan] = useState(null);
  const resolver = useRef(null);

  const check = async (data) => {
    if (!needsCheck(data)) return data;
    const id = toast.loading("Checking your scene timing...");
    try {
      const { data: r } = await api.post("/videos/scene-length", {
        script: data.script || "", visual_description: data.visual_description || data.trend_description || "", duration: data.duration || 15,
      }, { timeout: 60000 });
      toast.dismiss(id);
      if (!r.parts?.length) return data;
      if (!r.longer) return { ...data, scene_parts: r.parts };
      const fit = await new Promise((resolve) => { resolver.current = resolve; setPlan(r); });
      setPlan(null);
      return { ...data, scene_parts: r.parts, fit_length: fit };
    } catch {
      toast.dismiss(id);
      return data;
    }
  };

  const choose = (fit) => resolver.current?.(fit);
  const dialog = plan && (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="max-w-md [&>button]:hidden" data-testid="length-choice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-[#00F0FF]" />Your scene needs a bit more time</DialogTitle>
          <DialogDescription data-testid="length-choice-summary">
            The actions plus every line come to about {plan.estimated_seconds}s, but you picked {plan.target_seconds}s. Every line is kept either way.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Choice testId="length-choice-exact" icon={Scissors} title={`Keep it ${plan.target_seconds}s`}
            text="The silent action moments (walking, ordering...) get shorter." onClick={() => choose("exact")} />
          <Choice testId="length-choice-longer" icon={Timer} title={`Make it about ${plan.estimated_seconds}s`}
            text="The full action plays, so the video runs a little longer." onClick={() => choose("longer")} />
        </div>
      </DialogContent>
    </Dialog>
  );
  return { check, dialog };
};
